/**
 * The account page (wave b12 frontend item 3; wave COM item 4): who the caller is signed
 * in as, and the one preference the server keeps, `email_opt_out`.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The page reads GET /api/me afresh (the shell's copy was read at sign-in and would not
 * show a change made elsewhere) and writes through PATCH /api/me/preferences. The server
 * answers `changed: false` when the account already read the value; the page says so
 * instead of claiming a save. A refusal is said inside the form in the server's words.
 */

import { useState } from "react";

import { ApiError } from "../api/client";
import { fetchMe, setPreferences, type Me } from "../api/me";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { ServerErrorScope, ServerField } from "../lib/serverErrors";
import { ToastBar } from "../nav/ActionDialog";
import { Facts, RefusalBox, Section, SkeletonFacts } from "./parts";
import { useQuery } from "./useQuery";

export function AccountPage(): React.JSX.Element {
  const token = useToken();
  const me = useQuery((signal) => fetchMe(token, signal), [token]);
  const copy = strings.account;
  return (
    <>
      <h2 className="q-shell__heading">{copy.heading}</h2>
      <p className="q-shell__lede">{copy.lede}</p>
      {me.kind === "loading" && <SkeletonFacts fields={3} />}
      {(me.kind === "failed" || me.kind === "absence") && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.common.failed}</p>
        </div>
      )}
      {me.kind === "ready" && <AccountBody me={me.data} />}
    </>
  );
}

function AccountBody({ me }: { readonly me: Me }): React.JSX.Element {
  const token = useToken();
  const copy = strings.account;
  const [saved, setSaved] = useState(me.emailOptOut === true);
  const [optOut, setOptOut] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const answer = await setPreferences(token, optOut);
      setSaved(answer.emailOptOut);
      setOptOut(answer.emailOptOut);
      setToast(answer.changed ? copy.saved : copy.unchanged);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="q-section-bands" data-testid="account-page">
      {toast !== null && (
        <ToastBar
          message={toast}
          onDone={() => {
            setToast(null);
          }}
        />
      )}
      <Section heading={copy.identityHeading}>
        <Facts
          entries={[
            [copy.nameLabel, <span key="name">{me.name}</span>],
            [copy.usernameLabel, <span key="username">{me.username}</span>],
            [
              copy.emailLabel,
              <span key="email" data-testid="account-email">
                {me.email ?? copy.noEmail}
              </span>,
            ],
          ]}
        />
      </Section>
      <Section heading={copy.preferencesHeading}>
        <ServerErrorScope error={error}>
          <div className="q-subform" data-testid="account-preferences">
            <p className="q-note" data-testid="account-email-state">
              {saved ? copy.emailOff : copy.emailOn}
            </p>
            <label className="q-check">
              <ServerField field="email_opt_out">
                <input
                  type="checkbox"
                  id="account-email-opt-out"
                  data-testid="account-email-opt-out"
                  aria-describedby="account-email-opt-out-hint"
                  checked={optOut}
                  onChange={(event) => {
                    setOptOut(event.target.checked);
                  }}
                />
              </ServerField>{" "}
              {copy.emailOptOutLabel}
            </label>
            <p className="q-note" id="account-email-opt-out-hint">
              {copy.emailOptOutHint}
            </p>
            {error !== null && <RefusalBox error={error} />}
            <div className="q-actions-row">
              <button
                type="button"
                className="q-button q-mini--primary"
                data-testid="account-save"
                disabled={busy}
                onClick={() => {
                  void save();
                }}
              >
                {busy ? copy.saving : copy.save}
              </button>
            </div>
          </div>
        </ServerErrorScope>
      </Section>
    </div>
  );
}
